import React, { useState } from "react";
import ReactDOM from "react-dom";
import { getTranslation, formatGregorianDate } from "../../utils/translations";
import ResponsiveImage from "../ResponsiveImage";
import CircularProgress from "../CircularProgress/CircularProgress";
import IframeModal from "../Common/IframeModal";
import PhotoGalleryModal from "../Common/PhotoGalleryModal";
import PDFViewerModal from "../Common/PDFViewerModal";
import VideoModal from "../Common/VideoModal";
import Lottie from "lottie-react";

// Import Lottie animations
import liveAnimation from "../../assets/animations/18 live_opt.json";
import tour360Animation from "../../assets/animations/360-view-white.json";
import photoGalleryAnimation from "../../assets/animations/09-Photo gallery_opt.json";
import planAnimation from "../../assets/animations/plan-white-animation.json";
import videoAnimation from "../../assets/animations/video-white-animation.json";
import navigationAnimation from "../../assets/animations/30 Direction_opt.json";

import "./SearchProjectCard.css";

const SearchProjectCard = ({ project, language, onProjectSelect }) => {
  const [iframeModalOpen, setIframeModalOpen] = useState(false);
  const [iframeUrl, setIframeUrl] = useState("");
  const [iframeTitle, setIframeTitle] = useState("");
  const [photoGalleryOpen, setPhotoGalleryOpen] = useState(false);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);

  const formatPrice = (price) => {
    if (!price) return "N/A";
    return new Intl.NumberFormat("en-US").format(price);
  };

  const handleCardClick = () => {
    if (onProjectSelect) {
      onProjectSelect(project);
    }
  };

  const handleVirtualTourClick = (e) => {
    e.stopPropagation();
    if (project.virtual_tour_url) {
      const isMobile = typeof navigator !== "undefined" &&
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      if (isMobile) {
        window.open(project.virtual_tour_url, "_blank", "noopener,noreferrer");
        return;
      }
      setIframeUrl(project.virtual_tour_url);
      setIframeTitle(language === "ar" ? "جولة افتراضية 360°" : "360° Virtual Tour");
      setIframeModalOpen(true);
    }
  };

  const handleLiveCameraClick = (e) => {
    e.stopPropagation();
    if (project.live_camera_url) {
      setIframeUrl(project.live_camera_url);
      setIframeTitle(language === "ar" ? "البث المباشر" : "Live Stream");
      setIframeModalOpen(true);
    }
  };

  const handlePhotoGalleryClick = (e) => {
    e.stopPropagation();
    if (project.photo_gallery_image_urls && project.photo_gallery_image_urls.length > 0) {
      setPhotoGalleryOpen(true);
    }
  };

  const handleBrochureClick = (e) => {
    e.stopPropagation();
    if (project.project_brochure_pdf_url || project.project_brochure_pdf) {
      setPdfModalOpen(true);
    }
  };

  const handleVideoClick = (e) => {
    e.stopPropagation();
    if (project.promotional_video_url || project.promotional_video) {
      setVideoModalOpen(true);
    }
  };

  const handleShareClick = (e) => {
    e.stopPropagation();
    const baseUrl = project.share_url || window.location.origin + window.location.pathname;
    const shareUrl = `${baseUrl}?project=${project.id}&shared=true`;

    if (navigator.share) {
      navigator.share({
        title: project.name,
        text: "",
        url: shareUrl,
      }).catch(() => {
        fallbackShare(shareUrl);
      });
    } else {
      fallbackShare(shareUrl);
    }
  };

  const fallbackShare = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      alert(language === "ar" ? "تم نسخ الرابط إلى الحافظة" : "Link copied to clipboard");
    }).catch(() => {
      window.open(`https://wa.me/?text=${encodeURIComponent(url)}`, '_blank');
    });
  };

  const handleNavigationClick = (e) => {
    e.stopPropagation();
    if (project.latitude && project.longitude) {
      const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${project.latitude},${project.longitude}&travelmode=driving`;
      window.open(googleMapsUrl, '_blank');
    } else {
      const searchQuery = encodeURIComponent(`${project.name} ${project.location || ''}`);
      window.open(`https://www.google.com/maps/search/${searchQuery}`, '_blank');
    }
  };

  return (
    <>
      <div
        className={`search-project-card ${language === "ar" ? "rtl" : "ltr"}`}
        dir={language === "ar" ? "rtl" : "ltr"}
        onClick={handleCardClick}
      >
        {/* Desktop 3-Column Layout: Image | Details | Action Buttons */}
        <div className="spc-container">
          {/* Column 1: Image */}
          <div className="spc-image-column">
            <ResponsiveImage
              desktopImage={project.project_image_desktop_url}
              mobileImage={project.project_image_mobile_url}
              fallbackImage={project.default_unit_image_url}
              alt={project.name}
              className="spc-project-image"
              layout="desktop-side"
              aspectRatio="auto"
              desktopAspectRatio="auto"
              mobileAspectRatio="auto"
              language={language}
            />

            {/* Developer Logo */}
            {project.developer_logo_url && (
              <div className="spc-developer-logo">
                <img
                  src={project.developer_logo_url}
                  alt={project.developer_name || "Developer Logo"}
                  className="spc-developer-logo-img"
                />
              </div>
            )}
          </div>

          {/* Column 2: Details */}
          <div className="spc-details-column">
            <div className="spc-detail-item spc-title-detail">
              <div className="spc-title-with-status">
                <strong className="spc-title-text">
                  {language === "ar"
                    ? (project.name_ar || project.name)
                    : (project.name_en || project.name)}
                </strong>
                {project.status && (
                  <div className="spc-status-wrapper">
                    <span
                      className={`spc-status-badge ${project.status}`}
                      style={{ color: project.status_color }}
                    >
                      {language === "ar"
                        ? (project.status_label_ar || project.status)
                        : (project.status_label_en || project.status)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="spc-detail-item">
              <strong>{language === "ar" ? "عدد الوحدات:" : "Number of Units:"}</strong>
              {" "}{project.statistics?.total_units || 0} {language === "ar" ? "وحدة" : "units"}
            </div>

            <div className="spc-detail-item">
              <strong>{language === "ar" ? "يبدأ من:" : "Starting from:"}</strong>
              {" "}{formatPrice(project.statistics?.min_price || 0)}
              <img src="/map_controls/Saudi_Riyal_Symbol v2.png" alt="SAR" className="spc-riyal-icon" />
            </div>

            <div className="spc-detail-item">
              <strong>{language === "ar" ? "المرافق:" : "Facilities:"}</strong>
              {" "}{project.facilities ||
                project.facilities_ar ||
                project.facilities_en ||
                (language === "ar" ? "مساجد - مدارس - مواقف - حدائق" : "Mosques - Schools - Parking - Gardens")}
            </div>

            <div className="spc-detail-item">
              <strong>{language === "ar" ? "المطور:" : "Developer:"}</strong>
              {" "}{project.owner ||
                project.owner_name ||
                project.developer_name ||
                (language === "ar" ? project.owner_ar : project.owner_en) ||
                (language === "ar" ? " " : " ")}
            </div>

            {/* Timeline inside details column */}
            {(project.launch_date || project.completion_date || project.construction_progress > 0) && (
              <div className="spc-detail-item spc-timeline-detail">
                <div className="spc-timeline-section">
                  <div className="spc-timeline-items">
                    {project.launch_date && (
                      <div className="spc-timeline-item">
                        <div className="spc-timeline-value">
                          {formatGregorianDate(project.launch_date, language)}
                        </div>
                        <div className="spc-timeline-label">
                          {getTranslation("launchDate", language)}
                        </div>
                      </div>
                    )}
                    {project.completion_date && (
                      <div className="spc-timeline-item">
                        <div className="spc-timeline-value">
                          {formatGregorianDate(project.completion_date, language)}
                        </div>
                        <div className="spc-timeline-label">
                          {getTranslation("completionDate", language)}
                        </div>
                      </div>
                    )}
                  </div>

                  {project.construction_progress > 0 && (
                    <div className="spc-progress-section">
                      <CircularProgress
                        percentage={project.construction_progress}
                        size={55}
                        strokeWidth={4}
                        color="#4CAF50"
                        className="spc-progress"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Column 3: Action Buttons */}
          <div className="spc-buttons-column">
            <button className="spc-action-button" onClick={handleShareClick}>
              <img src="/share white v2.png" alt="Share" />
            </button>

            <button className="spc-action-button available" onClick={handleNavigationClick}
              title={language === "ar" ? "فتح في خرائط جوجل" : "Open in Google Maps"}>
              <Lottie animationData={navigationAnimation} loop autoplay style={{ width: 30, height: 30 }} />
            </button>

            {(project?.project_brochure_pdf_url || project?.project_brochure_pdf) && (
              <button className="spc-action-button available" onClick={handleBrochureClick}>
                <Lottie animationData={planAnimation} loop autoplay style={{ width: 35, height: 30 }} />
              </button>
            )}

            {(project?.promotional_video_url || project?.promotional_video) && (
              <button className="spc-action-button available" onClick={handleVideoClick}>
                <Lottie animationData={videoAnimation} loop autoplay style={{ width: 30, height: 30 }} />
              </button>
            )}

            {!!project.live_camera_url && (
              <button className="spc-action-button available" onClick={handleLiveCameraClick}>
                <Lottie animationData={liveAnimation} loop autoplay style={{ width: 30, height: 30 }} />
              </button>
            )}

            {project?.photo_gallery_image_urls && project.photo_gallery_image_urls.length > 0 && (
              <button className="spc-action-button available" onClick={handlePhotoGalleryClick}>
                <Lottie animationData={photoGalleryAnimation} loop autoplay style={{ width: 30, height: 30 }} />
              </button>
            )}

            {project?.virtual_tour_url && (
              <button className="spc-action-button available" onClick={handleVirtualTourClick}>
                <Lottie animationData={tour360Animation} loop autoplay style={{ width: 30, height: 30 }} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Modals - rendered via Portal to document.body so they appear above everything */}
      {ReactDOM.createPortal(
        <>
          <IframeModal
            isOpen={iframeModalOpen}
            onClose={() => { setIframeModalOpen(false); setIframeUrl(""); setIframeTitle(""); }}
            url={iframeUrl}
            title={iframeTitle}
            language={language}
          />

          <PhotoGalleryModal
            isOpen={photoGalleryOpen}
            onClose={() => setPhotoGalleryOpen(false)}
            images={project?.photo_gallery_image_urls || []}
            projectName={language === "ar" ? (project?.name_ar || project?.name) : (project?.name_en || project?.name)}
            language={language}
          />

          <PDFViewerModal
            isOpen={pdfModalOpen}
            onClose={() => setPdfModalOpen(false)}
            pdfUrl={project?.project_brochure_pdf_url || project?.project_brochure_pdf}
            title={language === "ar" ? "كتيب المشروع" : "Project Brochure"}
            language={language}
          />

          <VideoModal
            isOpen={videoModalOpen}
            onClose={() => setVideoModalOpen(false)}
            videoUrl={project?.promotional_video_url || project?.promotional_video}
            title={language === "ar" ? "الفيديو التعريفي" : "Promotional Video"}
            language={language}
          />
        </>,
        document.body
      )}
    </>
  );
};

export default SearchProjectCard;
